import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AnnouncementProvider } from './contexts/AnnouncementContext';
import AnnouncementModal from './components/AnnouncementModal';
import Login from './pages/Login';
import OIDCCallback from './pages/OIDCCallback';
import FileList from './pages/FileList';
import Upload from './pages/Upload';
import OTPVerification from './pages/OTPVerification';
import ReceivedFiles from './pages/ReceivedFiles';
import ShareDetail from './pages/ShareDetail';
import Contacts from './pages/Contacts';
import Forbidden from './pages/Forbidden';
import PasswordResetRequest from './pages/PasswordResetRequest';
import PasswordResetConfirm from './pages/PasswordResetConfirm';
import Settings from './pages/Settings';
import RequiredPasswordChange from './pages/RequiredPasswordChange';
import UserAnnouncements from './pages/Announcements';
import AdminRoute from './components/AdminRoute';
import { AdminLayout, UserManagement, UsageStats, ShareHistory, AuditLogs, Announcements } from './pages/admin';

interface ProtectedRouteProps {
  children: React.ReactElement;
}

/**
 * ProtectedRoute component
 * Redirects to /login if user is not authenticated
 * Redirects to /password-change/required if requirePasswordChange is true
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, requirePasswordChange } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requirePasswordChange) {
    return <Navigate to="/password-change/required" replace />;
  }

  return children;
};

/**
 * RequiredPasswordChangeRoute component
 * Only requires authentication, does NOT redirect for requirePasswordChange
 */
const RequiredPasswordChangeRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

/**
 * 認証済みのときのみ重要お知らせモーダルを表示する。
 * BrowserRouter 内に置くことで useNavigate を利用できる。
 */
const GlobalAnnouncementModal: React.FC = () => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return null;
  return <AnnouncementModal />;
};

function App() {
  return (
    <AuthProvider>
      <AnnouncementProvider>
        <BrowserRouter>
          <GlobalAnnouncementModal />
          <Routes>
          {/* Public route: Login */}
          <Route path="/login" element={<Login />} />

          {/* Public route: Password Reset */}
          <Route path="/password-reset" element={<PasswordResetRequest />} />
          <Route path="/password-reset/confirm" element={<PasswordResetConfirm />} />

          {/* Protected route: Required Password Change */}
          <Route
            path="/password-change/required"
            element={
              <RequiredPasswordChangeRoute>
                <RequiredPasswordChange />
              </RequiredPasswordChangeRoute>
            }
          />

          {/* Protected route: Settings */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          {/* メール内オプトアウトリンクの遷移先。設定ページに集約している。 */}
          <Route
            path="/settings/notifications"
            element={<Navigate to="/settings" replace />}
          />

          {/* Public route: OIDC Callback */}
          <Route path="/oidc-callback" element={<OIDCCallback />} />

          {/* Protected route: File List */}
          <Route
            path="/files"
            element={
              <ProtectedRoute>
                <FileList />
              </ProtectedRoute>
            }
          />

          {/* Protected route: Upload */}
          <Route
            path="/upload"
            element={
              <ProtectedRoute>
                <Upload />
              </ProtectedRoute>
            }
          />

          {/* Protected route: Received Files */}
          <Route
            path="/received"
            element={
              <ProtectedRoute>
                <ReceivedFiles />
              </ProtectedRoute>
            }
          />

          {/* Protected route: Share Detail */}
          <Route
            path="/shares/:shareId"
            element={
              <ProtectedRoute>
                <ShareDetail />
              </ProtectedRoute>
            }
          />

          {/* Protected route: Contacts */}
          <Route
            path="/contacts"
            element={
              <ProtectedRoute>
                <Contacts />
              </ProtectedRoute>
            }
          />

          {/* Protected route: Announcements (general user) */}
          <Route
            path="/announcements"
            element={
              <ProtectedRoute>
                <UserAnnouncements />
              </ProtectedRoute>
            }
          />

          {/* Public route: Receive file (share link) with OTP verification */}
          <Route path="/receive/:shareToken/verify" element={<OTPVerification />} />
          <Route path="/receive/:shareToken" element={<OTPVerification />} />

          {/* Forbidden page */}
          <Route path="/forbidden" element={<Forbidden />} />

          {/* Admin routes - protected with AdminRoute guard */}
          <Route path="/admin" element={<AdminRoute />}>
            <Route element={<AdminLayout />}>
              <Route index element={<Navigate to="/admin/stats" replace />} />
              <Route path="stats" element={<UsageStats />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="shares" element={<ShareHistory />} />
              <Route path="logs" element={<AuditLogs />} />
              <Route path="announcements" element={<Announcements />} />
            </Route>
          </Route>

          {/* Default route: Redirect to login */}
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* Catch-all: Redirect to login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </BrowserRouter>
      </AnnouncementProvider>
    </AuthProvider>
  );
}

export default App;
